// LandingPage.js
// React landing page with accessible skip link that works like Amazon
// (focuses + scrolls smoothly to main, without showing #hash in URL)

import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../components/LandingPage.css";

export default function LandingPage() {
  const navigate = useNavigate();
  const roles = ["team", "manager", "accountant", "crew", "company"];
  const [currentWord, setCurrentWord] = useState(0);
  const [tick, setTick] = useState(0);

  const [slotWidth, setSlotWidth] = useState(0);
  useEffect(() => {
        if (!measurerRef.current) return;
        measurerRef.current.textContent = roles[0];             // first word
        const w = measurerRef.current.getBoundingClientRect().width;
        setSlotWidth(Math.ceil(w));
    }, []); // run once

  const measurerRef = useRef(null);


  // --- Set up the skip-link handler when the component mounts ---
  useEffect(() => {
    const skip = document.getElementById("skip-link");
    const main = document.getElementById("main-content");
    if (!skip || !main) return;

    const handleSkip = (e) => {
      e.preventDefault(); // stop URL from showing #main-content
      main.setAttribute("tabindex", "-1"); // make focusable
      main.focus({ preventScroll: true });
      main.scrollIntoView({ behavior: "smooth", block: "start" });
      setTimeout(() => main.removeAttribute("tabindex"), 500);

      // optional: if hash somehow added, remove it quietly
      if (window.history.replaceState) {
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
      }
    };

    skip.addEventListener("click", handleSkip);
    return () => skip.removeEventListener("click", handleSkip);
  }, []);

   // rotate words
  useEffect(() => {
    const id = setInterval(() => {
      setCurrentWord(i => (i + 1) % roles.length);
      setTick(t => t + 1);
    }, 2200);
    return () => clearInterval(id);
  }, []);

  // measure width of the current word and animate slot width
  useEffect(() => {
    if (!measurerRef.current) return;
    measurerRef.current.textContent = roles[currentWord];
    const w = measurerRef.current.getBoundingClientRect().width;
    setSlotWidth(Math.ceil(w));
  }, [currentWord]);



  return (
    <div className="lp">
      {/* ======= Top Nav (landing-only, separate from app shell) ======= */}
      <a id="skip-link" href="#main-content" className="sr-only-focus">
        Skip to main content
      </a>

      <nav className="lp__nav">
        <div className="lp__container lp__navRow">
          {/* Left: brand */}
          <div className="lp__brand">
            <div className="sidebar-logo">
              <img src="/ProjexSiteWebLogo.png" alt="Projex Site Logo" />
            </div>
          </div>

          {/* Middle: links */}
          <div className="lp__navLinks">
            <a href="#features">Products</a>
            <Link to="/pricing">Plans & Pricing</Link>
            <a href="#features">Top features</a>
            <a href="#resources">Resources</a>
          </div>

          {/* Right: auth actions */}
          <div className="lp__navActions">
            <button className="lpBtn lpBtn--ghost" onClick={() => navigate("/dashboard")}>
              Get started
            </button>
            <button className="lpBtn lpBtn--danger" onClick={() => { /* add Google sign-in later */ }}>
              Sign in
            </button>
          </div>
        </div>
      </nav>

      {/* ======= Main Content ======= */}
      <main id="main-content">
        {/* ======= HERO ======= */}
        <header className="lp__hero" id="home">
          <div className="lp__container lp__heroGrid">
            <div>
              <h1 className="lp__title">
                    Your construction{" "}
                <span
                    className="lp__wordSlot lp__wordSlot--auto"
                    style={{ width: slotWidth ? `${slotWidth}px` : undefined }}
                    aria-live="polite"
                    aria-atomic="true"
                >
                    <span key={tick} className="lp__changingWord--drop lp__wordActual">
                    {roles[currentWord]}
                    </span>
                </span>{" "}
                does <span className="lp__gradientWord">virtually everything</span> with clarity.
                {/* hidden measurer anywhere in the component tree */}
                <span ref={measurerRef} className="lp__wordMeasurer" aria-hidden="true" />
                </h1>

                <p className="lp__subtitle">
                    Backed by photo timelines, blueprint overlays, and roles, ProjexSite helps you save time, make smart decisions,
                    and track progress with confidence.
                </p>
              <div className="lp__ctaRow">
                <button className="lpBtn lpBtn--primary" onClick={() => navigate("/dashboard")}>
                  Get started
                </button>
                <Link className="lpBtn lpBtn--outline" to="/pricing">
                  See plans & pricing
                </Link>
              </div>
              <div className="lp__hint">No credit card required • Cancel anytime</div>
            </div>

            {/* Right: floating chips */}
            <div className="lp__chips">
              {["Blueprints", "Analytics", "AI Assistant", "Financials", "Map View", "Messages"].map((t, i) => (
                <div className={`lp__chip lp__chip--${i}`} key={t}>
                  <span className="lp__chipDot" />
                  {t}
                </div>
              ))}
            </div>
          </div>
        </header>

        {/* ======= FEATURES ======= */}
        <section className="lp__section" id="features">
          <div className="lp__container">
            <div className="lp__sectionHead">
              <h2 className="lp__h2">What ProjexSite can do</h2>
              <p className="lp__muted">
                Visual tracking that mirrors how construction actually happens — by rooms, walls, and timelines.
              </p>
            </div>

            <div className="lp__cards">
              <article className="lp__card">
                <div className="lp__cardNum">01</div>
                <h3 className="lp__h3">Photo-first progress logs</h3>
                <p className="lp__mutedSm">
                  Upload photos, tag interior/exterior, and auto-group by room, wall, and month.
                </p>
              </article>
              <article className="lp__card">
                <div className="lp__cardNum">02</div>
                <h3 className="lp__h3">Blueprint overlays</h3>
                <p className="lp__mutedSm">
                  Drop markers on blueprint images. Tap a room to see its full photo history and notes.
                </p>
              </article>
              <article className="lp__card">
                <div className="lp__cardNum">03</div>
                <h3 className="lp__h3">Team & roles</h3>
                <p className="lp__mutedSm">
                  Invite managers and subs with proper access. Keep private projects secured.
                </p>
              </article>
            </div>
          </div>
        </section>
      </main>

      {/* ======= FOOTER ======= */}
      <footer className="lp__footer">
        <div className="lp__container lp__footerRow">
          <div className="lp__brand">
            <span className="lp__pin" />
            <span>© {new Date().getFullYear()} ProjexSite</span>
          </div>
          <div className="lp__footerLinks">
            <a href="#privacy">Privacy</a>
            <a href="#terms">Terms</a>
            <a href="#contact">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
